import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useCreateFormulaEntry } from '../hooks/use-create-formula-entry';
import { useUpdateFormulaEntry } from '../hooks/use-update-formula-entry';
import { FormulaLibraryEntry, FormulaScope } from '../types';

const MEASURE_TYPES = [
  { value: 'hours', label: 'Hours' },
  { value: 'money', label: 'Money' },
  { value: 'percent', label: 'Percent' },
  { value: 'number', label: 'Number' },
];

const FUNCTIONS = [
  // Math
  { key: 'round', label: 'round()', description: 'Round to nearest integer' },
  { key: 'abs', label: 'abs()', description: 'Absolute value' },
  { key: 'ceil', label: 'ceil()', description: 'Round up to nearest integer' },
  { key: 'floor', label: 'floor()', description: 'Round down to nearest integer' },
  { key: 'trunc', label: 'trunc()', description: 'Truncate decimal part (toward zero)' },
  { key: 'min', label: 'min(a, b)', description: 'Smaller of two values' },
  { key: 'max', label: 'max(a, b)', description: 'Larger of two values' },
  // Conditional
  { key: 'if', label: 'if(cond, a, b)', description: 'if(x > 0, x, 0) → CASE WHEN' },
  {
    key: 'switch',
    label: 'switch(val, c1, r1, default)',
    description: 'Multi-branch: switch(status, 1, 10, 2, 20, 0)',
  },
  { key: 'ifnull', label: 'ifnull(a, b)', description: 'Return b when a is null' },
  { key: 'ifempty', label: 'ifempty(a, b)', description: 'Return b when a is null or zero' },
  // Logical (for use inside if() conditions)
  { key: 'and', label: 'and(a, b)', description: 'Logical AND — use inside if()' },
  { key: 'or', label: 'or(a, b)', description: 'Logical OR — use inside if()' },
  { key: 'not', label: 'not(x)', description: 'Logical NOT — use inside if()' },
  // Null handling
  { key: 'coalesce', label: 'coalesce(a, b)', description: 'First non-null value' },
];

const FUNCTION_KEYS = new Set(FUNCTIONS.map(f => f.key));

function extractRefs(formulaString: string): string[] {
  const matches = formulaString.match(/\b([a-z_][a-z0-9_]*)\b/g) ?? [];
  return [...new Set(matches)].filter(r => !FUNCTION_KEYS.has(r));
}

function labelToKey(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getWordAtCursor(
  text: string,
  cursorPos: number
): { word: string; start: number; end: number } {
  const before = text.slice(0, cursorPos);
  const after = text.slice(cursorPos);
  const matchBefore = before.match(/[a-z_][a-z0-9_]*$/i);
  const matchAfter = after.match(/^[a-z0-9_]*/i);
  const wordBefore = matchBefore ? matchBefore[0] : '';
  const wordAfter = matchAfter ? matchAfter[0] : '';
  return {
    word: (wordBefore + wordAfter).toLowerCase(),
    start: cursorPos - wordBefore.length,
    end: cursorPos + wordAfter.length,
  };
}

interface FormulaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableColumns: Array<{ key: string; label: string }>;
  entry?: FormulaLibraryEntry;
}

export const FormulaDialog = ({
  open,
  onOpenChange,
  availableColumns,
  entry,
}: FormulaDialogProps) => {
  const { t } = useTranslation('authenticated.reports');
  const isEdit = !!entry;

  const [label, setLabel] = useState('');
  const [formulaString, setFormulaString] = useState('');
  const [measureType, setMeasureType] = useState('number');
  const [scope, setScope] = useState<'USER' | 'ORG'>('USER');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef(0);
  const [suggestions, setSuggestions] = useState<
    Array<{ key: string; label: string; isFunction: boolean }>
  >([]);

  useEffect(() => {
    if (open) {
      if (entry) {
        setLabel(entry.label);
        setFormulaString(entry.formulaString);
        setMeasureType(entry.measureType);
        setScope(entry.scope === FormulaScope.ORG ? 'ORG' : 'USER');
      } else {
        setLabel('');
        setFormulaString('');
        setMeasureType('number');
        setScope('USER');
      }
    }
  }, [open, entry]);

  const { mutate: createEntry, isPending: isCreating } = useCreateFormulaEntry();
  const { mutate: updateEntry, isPending: isUpdating } = useUpdateFormulaEntry();
  const isPending = isCreating || isUpdating;

  const availableColumnKeySet = useMemo(
    () => new Set(availableColumns.map(c => c.key)),
    [availableColumns]
  );

  const validationErrors = useMemo(() => {
    if (!formulaString.trim()) return [];
    return extractRefs(formulaString)
      .filter(ref => !availableColumnKeySet.has(ref))
      .map(ref => t('formula.unknownColumn', { column: ref }));
  }, [formulaString, availableColumnKeySet, t]);

  const dependsOn = useMemo(() => extractRefs(formulaString), [formulaString]);
  const canSubmit = label.trim() && formulaString.trim() && validationErrors.length === 0;

  const computeSuggestions = (text: string, pos: number) => {
    const { word } = getWordAtCursor(text, pos);
    if (word.length === 0) {
      setSuggestions([]);
      return;
    }
    const fnMatches = FUNCTIONS.filter(f => f.key.startsWith(word)).map(f => ({
      key: f.key,
      label: f.label,
      isFunction: true,
    }));
    const colMatches = availableColumns
      .filter(c => c.key.includes(word) || c.label.toLowerCase().includes(word))
      .slice(0, 6)
      .map(c => ({ key: c.key, label: c.label, isFunction: false }));
    setSuggestions([...fnMatches, ...colMatches].slice(0, 8));
  };

  const insertItem = (item: { key: string; isFunction: boolean }) => {
    const pos = cursorPosRef.current;
    const { start, end } = getWordAtCursor(formulaString, pos);
    const insertion = item.isFunction ? `${item.key}()` : item.key;
    const newValue = formulaString.slice(0, start) + insertion + formulaString.slice(end);
    setFormulaString(newValue);
    setSuggestions([]);
    // For functions, place cursor inside parens; for columns, after the key
    const newCursorPos = item.isFunction ? start + insertion.length - 1 : start + insertion.length;
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        cursorPosRef.current = newCursorPos;
      }
    });
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? 0;
    cursorPosRef.current = pos;
    setFormulaString(val);
    computeSuggestions(val, pos);
  };

  const handleTextareaSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const pos = e.currentTarget.selectionStart ?? 0;
    cursorPosRef.current = pos;
    computeSuggestions(formulaString, pos);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (isEdit) {
      updateEntry(
        { id: entry.id, label: label.trim(), formulaString: formulaString.trim(), dependsOn },
        { onSuccess: () => onOpenChange(false) }
      );
    } else {
      createEntry(
        {
          scope,
          key: labelToKey(label),
          label: label.trim(),
          formulaString: formulaString.trim(),
          dependsOn,
          measureType,
        },
        { onSuccess: () => onOpenChange(false) }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('formula.editTitle') : t('formula.newTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>{t('formula.label')}</Label>
            <Input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder={t('formula.labelPlaceholder')}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('formula.formula')}</Label>

            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={formulaString}
                onChange={handleTextareaChange}
                onSelect={handleTextareaSelect}
                onBlur={() => setTimeout(() => setSuggestions([]), 150)}
                onFocus={e => {
                  cursorPosRef.current = e.currentTarget.selectionStart ?? 0;
                  computeSuggestions(formulaString, e.currentTarget.selectionStart ?? 0);
                }}
                placeholder={t('formula.formulaPlaceholder')}
                className="h-20 resize-none font-mono text-sm"
              />

              {suggestions.length > 0 && (
                <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
                  {suggestions.map(s => (
                    <button
                      key={s.key}
                      type="button"
                      onMouseDown={e => {
                        e.preventDefault();
                        insertItem(s);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                    >
                      {s.isFunction ? (
                        // eslint-disable-next-line i18next/no-literal-string
                        <span className="shrink-0 rounded bg-primary/10 px-1 py-px font-mono text-xs text-primary">
                          fn
                        </span>
                      ) : (
                        // eslint-disable-next-line i18next/no-literal-string
                        <span className="shrink-0 rounded bg-muted px-1 py-px font-mono text-xs text-muted-foreground">
                          col
                        </span>
                      )}
                      <span className="font-mono text-xs">{s.isFunction ? s.label : s.key}</span>
                      {!s.isFunction && (
                        <span className="truncate text-xs text-muted-foreground">{s.label}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {validationErrors.length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {validationErrors.map(err => (
                  <li key={err} className="text-xs text-destructive">
                    {err}
                  </li>
                ))}
              </ul>
            )}

            {/* Clickable column chips */}
            {availableColumns.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-medium text-muted-foreground">{t('formula.columns')}</p>
                <div className="flex flex-wrap gap-1">
                  {availableColumns.map(col => (
                    <button
                      key={col.key}
                      type="button"
                      title={col.label}
                      onMouseDown={e => {
                        e.preventDefault();
                        insertItem({ key: col.key, isFunction: false });
                      }}
                      className="rounded border px-2 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                    >
                      {col.key}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Clickable function chips */}
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-medium text-muted-foreground">{t('formula.functions')}</p>
              <div className="flex flex-wrap gap-1">
                {FUNCTIONS.map(fn => (
                  <button
                    key={fn.key}
                    type="button"
                    title={fn.description}
                    onMouseDown={e => {
                      e.preventDefault();
                      insertItem({ key: fn.key, isFunction: true });
                    }}
                    className="rounded border px-2 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                  >
                    {fn.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {!isEdit && (
            <>
              <div className="flex flex-col gap-2">
                <Label>{t('formula.measureType')}</Label>
                <Select value={measureType} onValueChange={setMeasureType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEASURE_TYPES.map(mt => (
                      <SelectItem key={mt.value} value={mt.value}>
                        {t(`formula.type.${mt.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label>{t('formula.scope')}</Label>
                <Select value={scope} onValueChange={v => setScope(v as 'USER' | 'ORG')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USER">{t('formula.scopeUser')}</SelectItem>
                    <SelectItem value="ORG">{t('formula.scopeOrg')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
